import { domOutputSpecToHTMLString } from '@/lib/tiptap-static-renderer/pm/html-string/html-string';
import { getSchema, type Extensions } from '@tiptap/core';
import { createColGroup } from '@tiptap/extension-table/table';
import { Node, type Schema } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import type { JSONContent } from '@tiptap/react';

export function parseImportedWidth(element: HTMLElement): number | null {
    const widthAttr = element.getAttribute('width');
    if (widthAttr) {
        const match = widthAttr.trim().match(/^(\d+(?:\.\d+)?)/);
        if (match) {
            return Number(match[1]);
        }
    }

    const styleWidth = element.style.width?.trim();
    if (styleWidth) {
        const match = styleWidth.match(/^(\d+(?:\.\d+)?)%?$/);
        if (match) {
            return Number(match[1]);
        }
    }

    return null;
}

/**
 * Renders a table colgroup for zyBooks export.
 * Uses TipTap's createColGroup when cells have colwidth;
 */
export const renderTableColgroupHtml = (tableNode: JSONContent, extensions: Extensions): string => {
    const schema = getSchema(extensions);
    const pmTable = Node.fromJSON(schema, tableNode);
    const colGroupResult = createColGroup(pmTable, 40);
    if (!('colgroup' in colGroupResult)) {
        return '';
    }

    return domOutputSpecToHTMLString(colGroupResult.colgroup)();
};

const TABLE_HEADER_ONLY_ATTRS = ['scope', 'primaryKey', 'foreignKey'] as const;

/**
 * Converts every tableHeader cell in a table to tableCell.
 * Layout grids must not retain semantic header rows or columns.
 */
export function convertTableHeadersToCells(tr: Transaction, tablePos: number, tableNode: Node, schema: Schema): void {
    const tableCellType = schema.nodes.tableCell;
    if (!tableCellType) {
        return;
    }

    tableNode.descendants((node, relativePos) => {
        if (node.type.name !== 'tableHeader') {
            return;
        }

        const cellAttrs = { ...node.attrs };
        for (const attr of TABLE_HEADER_ONLY_ATTRS) {
            delete cellAttrs[attr];
        }

        tr.setNodeMarkup(tablePos + 1 + relativePos, tableCellType, cellAttrs);
    });
}
